/**
 * The app controller (web-client.md §1 `app/`). It wires the DOM-free core — auth → REST → WsSession
 * → Store — to the DOM UI (Shell), and implements the {@link AppApi} intent surface the views call.
 * The access token lives only in the {@link TokenManager} (in memory); it is never persisted.
 */
import {
  AuthClient,
  CommandLog,
  DeliberateChoice,
  HttpClient,
  PROTOCOL_VERSION,
  PersistenceWorker,
  ProtocolError,
  SessionClient,
  SettingsStore,
  Store,
  TokenManager,
  VersionClient,
  WsSession,
  NoticeCodes,
  decideUpdate,
  nameReadsAsCommand,
  openSessionCache,
  rawWebSocketTransportFactory,
  buildJoinLink,
  withoutJoinLink,
} from '../core/index.ts';
import type {
  ApiError,
  NoticePayload,
  PolicyDraft,
  GameActionPayload,
  JoinIntent,
  SessionCache,
  ScenarioCard,
  SessionListItem,
} from '../core/index.ts';
import { Outbox } from '../core/index.ts';
import { Shell } from '../ui/shell/index.ts';
import { setLanguage, t } from '../ui/i18n.ts';
import { refusalAction } from '../ui/views/refusals.ts';
import type { AppApi, ViewName } from '../ui/shell/index.ts';
import { CLIENT_TYPE, resolveConfig } from './config.ts';
import type { AppConfig } from './config.ts';
import { browserKeyValueStore } from './browserEnv.ts';
import { Pwa } from './pwa.ts';

const LOG_LIMIT = 50;

function short(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

/** True in a production build (Vite injects import.meta.env.PROD). False under dev/tests. */
function isProd(): boolean {
  const env = (import.meta as unknown as { env?: { PROD?: boolean } }).env;
  return env?.PROD === true;
}

function errMsg(error: unknown): string {
  if (error instanceof ProtocolError) return `${error.code} — ${error.message}`;
  if (error instanceof Error) return error.message;
  return String(error);
}

export class App implements AppApi {
  readonly store: Store;

  private readonly config: AppConfig;
  private readonly settings: SettingsStore;
  private readonly http: HttpClient;
  private readonly auth: AuthClient;
  private readonly tokens: TokenManager;
  private readonly sessions: SessionClient;
  private readonly versions: VersionClient;

  /** The newest version the server advertises for this client type; null until the manifest is read. */
  private latest: string | null = null;
  private readonly ws: WsSession;
  private readonly shell: Shell;
  private readonly pwa: Pwa;
  private readonly commandLog = new CommandLog();

  private cache: SessionCache | null = null;
  private persistence: PersistenceWorker | null = null;
  private publicList: SessionListItem[] = [];
  /** The sessions this player belongs to, mine-first in the lobby list (DEC-18). */
  private myList: SessionListItem[] = [];
  /** The packaged decks a session can be created from (DEC-17 M4 increment 8b). */
  private deckList: ScenarioCard[] = [];
  /**
   * The unconfirmed half of what this player has said. NOT in the store: the store is the fold of
   * committed events and is cached to IndexedDB for warm start, so an optimistic line written there
   * would come back after a reload as though it had been sent.
   */
  private readonly pending = new Outbox();
  private active: string | null = null;
  private logLines: string[] = [];
  private updateNotified = false;

  /**
   * Join codes this client has legitimately been shown - because it created the session, or because
   * it arrived on an invitation that carried one. Never fetched: the server hands a code to the owner
   * only (REC-2), and a client inventing one would just be guessing.
   */
  private readonly joinCodes = new Map<string, string>();

  /**
   * The "yes, really" for `login <name>`: minting a second identity, or taking a name that reads as a
   * command. One gate for both, because a player who has been shown two reasons to stop and typed the
   * same thing again has answered both of them.
   */
  private readonly newIdentity = new DeliberateChoice();

  /**
   * Cards this client has previewed, by id. Client-side and ephemeral on purpose: a preview is not
   * state, and a draft nobody adopts should leave nothing behind anywhere.
   */
  private readonly drafts = new Map<string, PolicyDraft['card'] & object>();

  constructor(
    mount: HTMLElement,
    language: string,
    /** A `#/join/...` invitation the entry point parsed out of the URL, if there was one. */
    private readonly invitation?: JoinIntent,
  ) {
    this.settings = new SettingsStore(browserKeyValueStore());
    this.config = resolveConfig(this.settings, language);
    // One language per session, decided before the first frame renders: the same value the wire already
    // carries on sys.hello and Accept-Language, so the interface and the NPC voices agree.
    setLanguage(this.config.language);
    this.store = new Store();

    this.http = new HttpClient({
      baseUrl: this.config.serverUrl,
      clientVersion: this.config.clientVersion,
      language: this.config.language,
      tokenProvider: () => this.tokens.getAccessToken(),
    });
    this.auth = new AuthClient(this.http);
    this.tokens = new TokenManager({
      authClient: this.auth,
      // Rotate the token on the live socket before it expires (protocol.md §4.6).
      onAccessToken: (token) => this.ws.reauth(token),
      onRefreshError: () => this.print(t('app.tokenRefreshFailed')),
    });
    this.sessions = new SessionClient(this.http);
    this.versions = new VersionClient(this.http);
    this.ws = new WsSession({
      url: this.config.wsUrl,
      transportFactory: rawWebSocketTransportFactory,
      store: this.store,
      tokenProvider: () => this.tokens.getAccessToken(),
      clientVersion: this.config.clientVersion,
      platform: this.config.platform,
      language: this.config.language,
      onAuthError: (e) => void this.handleAuthError(e),
      // Forced update (web-client.md §7): the server locked out our version — skipWaiting + hard reload.
      onUpgradeRequired: (e) => this.forceUpdate(e.message),
      onNotice: (n) => this.print(this.noticeLine(n)),
      onCommand: (rec) => this.commandLog.record(rec),
    });

    // PWA: register the SW (best-effort; prod only so the dev server's absent /sw.js is not fetched).
    this.pwa = new Pwa({
      enabled: isProd(),
      onUpdateReady: () => this.print(t('app.updateReady')),
    });

    this.shell = new Shell(mount, this);
    // Re-render on every store change (welcome, events, presence, connection status).
    this.store.subscribe(() => this.shell.render());
  }

  /** Render the first frame and focus the input. */
  start(): void {
    this.print(t('app.server', { url: this.config.serverUrl }));
    this.print(t('app.welcome'));
    this.shell.render();
    this.shell.focus();
    void this.pwa.register();
    void this.checkForUpdate();
    // The invitation runs AFTER the warm start so a cached view of this same session is already on
    // screen when the join lands, rather than flashing empty and then filling in.
    void this.warmStart().then(() =>
      this.invitation ? this.openFromLink(this.invitation) : undefined,
    );
  }

  /**
   * Cold-start warm start (web-client.md §3): open the IndexedDB cache, hydrate the last session view
   * READ-ONLY ("reconnecting…") until sys.welcome replaces it, then begin debounced persistence. The
   * access token is never cached, so a warm-started view is display-only until the user re-auths.
   */
  private async warmStart(): Promise<void> {
    try {
      this.cache = await openSessionCache();
      this.persistence = new PersistenceWorker(this.store, this.cache, {
        activeId: () => this.active,
      });
      const snapshot = await this.persistence.load();
      if (snapshot && snapshot.sessions.length) {
        this.store.hydrateFromCache(snapshot.sessions);
        this.active = snapshot.activeId;
        this.print(t('app.restored'));
      }
      this.persistence.start();
    } catch {
      /* warm start is best-effort; the app runs without it */
    }
  }

  /** Fetch the version manifest and act on the update decision (notify or force) (web-client.md §7). */
  private async checkForUpdate(): Promise<void> {
    try {
      const manifest = await this.versions.manifest();

      // Keep what the server says is current, so the terminal can show running-vs-latest rather than
      // only shouting when an update exists. "Up to date" is information too - it is the answer to
      // "am I looking at a stale build?", which is the first question anyone asks about a bug report.
      this.latest = manifest.clients[CLIENT_TYPE]?.latestVersion ?? null;

      const decision = decideUpdate({
        clientVersion: this.config.clientVersion,
        clientProtocol: PROTOCOL_VERSION,
        manifest,
        clientType: CLIENT_TYPE,
      });
      if (decision.action === 'force') {
        this.forceUpdate(decision.instructions ?? 'update required');
      } else if (decision.action === 'notify' && !this.updateNotified) {
        this.updateNotified = true;
        this.print(t('app.updateAvailable', { instructions: decision.instructions ?? '' }));
      }
    } catch {
      /* offline / manifest unavailable — non-fatal */
    }
  }

  private forceUpdate(reason: string): void {
    this.print(t('app.updateRequired', { reason }));
    this.pwa.forceReload();
  }

  // ---- AppApi: reads ------------------------------------------------------------------------------

  clientVersion(): string {
    return this.config.clientVersion;
  }

  /** What the server says is newest for this client, or null until the manifest has been read. */
  latestVersion(): string | null {
    return this.latest;
  }

  playerId(): string | null {
    return this.tokens.getPlayerId() ?? this.store.getState().identity.playerId;
  }
  isAuthenticated(): boolean {
    return this.tokens.isAuthenticated();
  }
  connectionStatus(): string {
    const c = this.store.getConnection();
    return c.attempt > 0 ? `${c.status} (attempt ${c.attempt})` : c.status;
  }
  publicSessions(): SessionListItem[] {
    return this.publicList;
  }
  mySessions(): SessionListItem[] {
    return this.myList;
  }
  scenarios(): ScenarioCard[] {
    return this.deckList;
  }
  activeSessionId(): string | null {
    return this.active;
  }
  log(): string[] {
    return this.logLines;
  }
  /** True during warm start: the view is cache-hydrated and read-only until sys.welcome (§3). */
  isReadOnly(): boolean {
    return this.store.isHydratedFromCache();
  }

  // ---- AppApi: intents ----------------------------------------------------------------------------

  /**
   * One `sys.notice` as a terminal line.
   *
   * The server's sentence already names an action, so the default is simply to print it - a code this
   * build has never heard of is still readable. What the client adds is the half only it can spell:
   * taking a seat is `/claim <seat>` here and a click over in the workspace, and opening your own room
   * is `create <name> game`. messages.md §4 puts exactly that half in the client.
   */
  private noticeLine(n: NoticePayload): string {
    const message = n.message ?? '';
    const seat = n.seats?.[0];
    if (n.code === NoticeCodes.MentionNoSeat && seat) {
      return t('app.noticeClaim', { message, seat });
    }

    return n.code === NoticeCodes.JoinObserver
      ? t('app.noticeObserver', { message })
      : t('app.notice', { message });
  }

  /**
   * The wait a rate-limited refusal implies (standards/messages.md: the action has to be TRUE).
   *
   * "try again" is the right shape and the wrong advice when a limiter is holding the door: trying again
   * now fails again, and the player concludes the software is broken rather than that they are early.
   * The server already carries `retryAfterMs`; this turns it into the sentence.
   */
  private retryHint(error: unknown): string | null {
    if (!(error instanceof ProtocolError) || error.retryAfterMs === null) {
      return null;
    }

    return t('app.retryAfter', { seconds: Math.max(1, Math.ceil(error.retryAfterMs / 1000)) });
  }

  /**
   * Signs in - as the SAME player as last time when that is possible.
   *
   * The refresh token lives in an HttpOnly cookie the browser keeps across reloads, so a returning
   * player already has an identity; minting a fresh guest threw it away and gave them a new id, a new
   * name and no history every single time. That is the bug this fixes, and the fix is to ASK before
   * creating: try the cookie, and only mint when there is nothing to resume.
   *
   * A name typed with `login <name>` still wins - renaming yourself is a deliberate act, and it is the
   * one case where a new identity is what the player asked for.
   */
  async guestLogin(displayName: string): Promise<void> {
    const wanted = displayName.trim();
    try {
      if (wanted.length === 0) {
        this.newIdentity.clear();
        if (await this.resumeIdentity()) {
          return;
        }
      } else {
        // WARN, THEN GET OUT OF THE WAY (playtest one). Both of these are the player's call to make -
        // a silly name is still a name, and a second identity is sometimes exactly what somebody
        // wants - so the guard is a sentence and a second `login`, never a refusal.
        const warnings = await this.identityWarnings(wanted);
        if (warnings.length > 0 && !this.newIdentity.confirms(wanted)) {
          for (const warning of warnings) {
            this.print(warning);
          }

          this.print(t('app.identityConfirm', { name: wanted }));
          this.shell.render();
          return;
        }
      }

      this.newIdentity.clear();
      const res = await this.auth.guest(wanted || undefined);
      this.tokens.adopt(res);
      this.print(t('app.signedIn', { player: wanted || short(res.playerId) }));
      // A live socket is still holding the OLD identity's token, and `start()` is a no-op while it is
      // live - so switching identity has to re-handshake or the new player keeps the old one's rooms.
      if (this.ws.isLive()) {
        this.ws.reconnect();
      } else {
        this.ws.start();
      }

      await this.refreshSessions();
    } catch (error) {
      // standards/messages.md: the cause is usually the server or the network, neither of which the
      // player can inspect - so the action is the one thing they can do, plus where to look if it sticks.
      this.print(t('app.loginFailed', { error: errMsg(error) }));
      const wait = this.retryHint(error);
      if (wait) this.print(wait);
    }
    this.shell.render();
  }

  /**
   * What is worth saying before a NEW identity is minted under this name. Empty when there is nothing.
   *
   * The second half does something with a side effect on purpose: it RESUMES the identity the refresh
   * cookie holds. That is not a detour, it is the outcome the warning recommends - the player who
   * meant to come back as themselves is now back as themselves, and the one who meant to start over
   * types the name again.
   */
  private async identityWarnings(wanted: string): Promise<string[]> {
    const warnings: string[] = [];
    if (nameReadsAsCommand(wanted)) {
      warnings.push(t('app.nameLooksLikeCommand', { name: wanted }));
    }

    if (!this.tokens.isAuthenticated()) {
      await this.resumeIdentity();
    }

    const playerId = this.tokens.getPlayerId();
    if (playerId !== null) {
      warnings.push(
        t('app.identityExists', { player: short(playerId), teams: this.myList.length }),
      );
    }

    return warnings;
  }

  /**
   * Takes back the identity the refresh cookie still holds, if it holds one.
   *
   * Returns false rather than throwing when there is nothing to resume: a first-time player has no
   * cookie, and that is the ordinary case, not an error worth showing them.
   */
  private async resumeIdentity(): Promise<boolean> {
    try {
      await this.tokens.refreshNow();
    } catch {
      return false;
    }

    const playerId = this.tokens.getPlayerId();
    if (playerId === null) {
      return false;
    }

    this.print(t('app.resumed', { player: short(playerId) }));
    this.ws.start();
    await this.refreshSessions();
    this.shell.render();
    return true;
  }

  async createSession(
    name: string,
    visibility: 'public' | 'private',
    scenarioRef?: string,
  ): Promise<void> {
    if (!this.tokens.isAuthenticated()) return this.print(t('app.loginFirst'));
    try {
      const detail = await this.sessions.create({ name, visibility, scenarioRef });
      this.active = detail.id;
      // The owner is written into the session rather than joining it, so no event ever carries their
      // name: without this seed they are an id in their own room.
      this.store.seedMembers(detail.id, detail.members);
      this.print(t('app.created', { name: detail.name ?? '', id: detail.id }));
      // A private session returns its join code to the owner (REC-2) — surface it so it can be shared.
      if (detail.joinCode) {
        this.joinCodes.set(detail.id, detail.joinCode);
        this.print(t('app.joinCode', { code: detail.joinCode }));
        this.print(t('app.shareHint'));
      }
      // Re-handshake so sys.welcome subscribes to the new membership + plans its resume.
      this.ws.reconnect();
      this.goTo('session');
    } catch (error) {
      this.print(t('app.createFailed', { error: errMsg(error) }));
      const wait = this.retryHint(error);
      if (wait) this.print(wait);
      this.shell.render();
    }
  }

  async joinSession(target: string): Promise<void> {
    if (!this.tokens.isAuthenticated()) return this.print('login first (type "login")');
    const [id, joinCode] = target.trim().split(/\s+/);
    if (!id) return this.print(t('app.usage.join'));
    try {
      await this.sessions.join(id, joinCode);
      // Everyone already in the room, by name - the events only describe arrivals from here on.
      this.store.seedMembers(id, (await this.sessions.get(id)).members);
      this.active = id;
      this.print(t('app.joined', { id: short(id) }));
      this.ws.reconnect();
      this.goTo('session');
    } catch (error) {
      this.print(t('app.joinFailed', { error: errMsg(error) }));
      const wait = this.retryHint(error);
      if (wait) this.print(wait);
      this.shell.render();
    }
  }

  /**
   * Prints the link that brings another device into THIS session. A session is already multi-device -
   * the server is authoritative and every client resumes from its own last seq - so what a second
   * device needs is not a feature, it is an address.
   */
  shareSession(): void {
    const id = this.active;
    if (!id) return this.print(t('app.shareFirst'));

    // The code is only known to the owner (REC-2). For everyone else the link still works for a
    // PUBLIC session, and for a private one the code has to come from whoever owns it - saying so is
    // better than printing a link that quietly fails on the other device.
    const code = this.joinCodes.get(id);
    const link = buildJoinLink(
      this.origin(),
      code ? { sessionId: id, joinCode: code } : { sessionId: id },
    );

    this.print(t('app.shareLink', { link }));
    this.print(t('app.shareNote'));
    if (!code) {
      this.print(t('app.sharePrivate'));
    }

    void this.copyToClipboard(link);
    this.shell.render();
  }

  /**
   * Acts on a `#/join/...` link at boot: signs in as a guest if needed, joins, then REMOVES the link
   * from the address bar so the join code does not sit in browser history for the next person at
   * this screen.
   */
  private async openFromLink(intent: JoinIntent): Promise<void> {
    this.print(t('app.invitation', { id: short(intent.sessionId) }));
    if (!this.tokens.isAuthenticated()) {
      // A guest identity is created for exactly this reason: an invitation should be one click for
      // the person receiving it, not a signup.
      await this.guestLogin('');
    }

    if (intent.joinCode) {
      this.joinCodes.set(intent.sessionId, intent.joinCode);
    }

    await this.joinSession(
      intent.joinCode ? `${intent.sessionId} ${intent.joinCode}` : intent.sessionId,
    );

    const cleaned = withoutJoinLink(globalThis.location?.href ?? '');
    globalThis.history?.replaceState?.(null, '', cleaned);
  }

  private origin(): string {
    const loc = globalThis.location;
    return loc ? `${loc.origin}${loc.pathname}` : '';
  }

  // Best-effort: a clipboard write needs permission and a secure context, and a link that printed but
  // did not copy is still a link somebody can select.
  private async copyToClipboard(text: string): Promise<void> {
    try {
      await globalThis.navigator?.clipboard?.writeText(text);
      this.print(t('app.copied'));
      this.shell.render();
    } catch {
      /* not available: the printed link is the fallback, and it is the same link */
    }
  }

  async refreshSessions(): Promise<void> {
    try {
      // BOTH lists, in one refresh (DEC-18). "Where was I?" is the common case and "what's out there?"
      // is the rare one, so the answer to the first must never depend on remembering to ask for it.
      // Settled rather than awaited in sequence: a directory that is slow or empty must not stop your
      // own sessions appearing, and neither list is worth failing the other over.
      // The deck catalog rides the same refresh (DEC-17 M4 increment 8b): `create ... game <deck>`
      // completes and validates against it, so it has to be there before the first create rather than
      // fetched on demand at the moment somebody types. Swallowed on failure like `mine` - a directory
      // or a catalog that is slow must not stop the other lists appearing, and `game` still has its
      // default.
      const [mine, page, decks] = await Promise.all([
        this.sessions.listMine().catch(() => ({ items: [] as SessionListItem[] })),
        this.sessions.listPublic(),
        this.sessions.listScenarios().catch(() => ({ scenarios: [] as ScenarioCard[] })),
      ]);
      this.myList = mine.items;
      this.publicList = page.items;
      this.deckList = decks.scenarios;
      this.print(t('app.listed', { n: page.items.length, mine: mine.items.length }));
    } catch (error) {
      this.print(t('app.listFailed', { error: errMsg(error) }));
      const wait = this.retryHint(error);
      if (wait) this.print(wait);
    }
    this.shell.render();
  }

  /**
   * Say something, in two stages (the outbox).
   *
   * Stage one is the line appearing in the scrollback NOW, marked as going out. Stage two is the
   * committed event arriving through the store exactly as it always did - `AckPayload.seq` correlates
   * the two, so this needed no wire change and the rail never shows a line twice.
   *
   * The old comment here said a failed line "is absent from the log either way, and a player who
   * assumes it went out will not repeat themselves". That was true and it was the problem: the words
   * were gone too, so repeating themselves meant retyping from memory. Now the line stays, marked, and
   * `/retry` sends it again.
   */
  async sendChat(text: string): Promise<void> {
    if (this.isReadOnly()) return this.print(t('app.reconnecting'));
    if (!this.active) return this.print(t('app.noActiveSession'));
    await this.post(this.active, text);
  }

  private async post(session: string, text: string): Promise<void> {
    const cmdId = this.ws.newCmdId();
    this.pending.say(cmdId, session, null, text);
    this.shell.render();

    try {
      const ack = await this.ws.sendChat(session, text, undefined, cmdId);
      this.pending.accepted(cmdId, ack.seq ?? null);
    } catch (error) {
      this.pending.refused(cmdId, errMsg(error));
      const wait = this.retryHint(error);
      if (wait) this.print(wait);
    }

    this.shell.render();
  }

  outbox(): Outbox {
    return this.pending;
  }

  async retrySend(cmdId: string): Promise<void> {
    const line = this.pending.lineAt(cmdId);
    if (!line) return this.print(t('app.noSuchPending', { id: cmdId }));

    // A NEW command id: the refused one never committed, so there is nothing to deduplicate against
    // and reusing it would only risk replaying the refusal.
    this.pending.forget(cmdId);
    await this.post(line.session, line.text);
  }

  discardSend(cmdId: string): void {
    this.pending.forget(cmdId);
    this.shell.render();
  }

  async askNpc(seat: string, about: string, text?: string): Promise<void> {
    if (!this.active)
      return this.print('no active session – type "list" then a number, or: create <name> game');
    try {
      this.store.applyNpcReply(
        this.active,
        await this.sessions.askNpc(this.active, seat, about, text),
      );
    } catch (error) {
      // A refused topic is the normal case for a typo, not an error worth a stack trace.
      const message = errMsg(error);
      this.print(t('app.askFailed', { seat, error: message }));
      const action = refusalAction(message);
      if (action) {
        this.print(action);
      }
    }
  }

  /**
   * Compiles a free-text goal into key results (S2.4 §3.3). Prints suggestions and changes NOTHING: the
   * player adopts one by typing the /kr it names, which is the same command they could have written
   * unaided. That is what keeps a model out of the write path entirely.
   */
  /**
   * Compiles a working agreement and PREVIEWS it (S2.4 §4.2 steps 1-4).
   *
   * Nothing is adopted here. The card is held on the client until someone types /adopt, which is the
   * "no surprise adoptions" rule made mechanical: the table reads what the rule does - both halves of
   * it, benefit and cost - before it is anyone's rule.
   */
  async draftPolicy(text: string): Promise<void> {
    if (!this.active) return this.print(t('app.noActiveSession'));
    try {
      const draft = await this.sessions.draftPolicy(this.active, text);
      if (!draft.card) {
        if (draft.reasons.length === 0) {
          this.print(t('policy.unavailable'));
          return;
        }

        // The reasons are the useful half of a refusal: the player is the one who can change the idea.
        this.print(t('policy.refused'));
        for (const reason of draft.reasons) {
          this.print(t('policy.reason', { rule: reason.rule, message: reason.message }));
        }

        return;
      }

      this.drafts.set(draft.card.id, draft.card);
      const effect = (e: { metric: string; value: number }): string =>
        `${e.metric} ${e.value > 0 ? '+' : ''}${e.value}/round`;

      this.print(t('policy.preview', { name: draft.card.name, id: draft.card.id }));
      this.print(t('policy.prose', { prose: draft.card.prose }));
      this.print(t('policy.effects', { effects: draft.card.effects.map(effect).join(' · ') }));
      this.print(
        t('policy.downside', { effects: draft.card.downsideEffects.map(effect).join(' · ') }),
      );
      this.print(t('policy.adopt', { id: draft.card.id }));
    } catch (error) {
      const message = errMsg(error);
      this.print(t('policy.failed', { error: message }));
      const wait = this.retryHint(error);
      if (wait) this.print(wait);
    }
  }

  /**
   * Adopts a previewed card. The whole card goes on the wire, not just its id: the engine re-validates
   * it and the LOG keeps the scalars, which is what lets replay re-apply the rule with no model.
   */
  async adoptPolicy(id: string): Promise<void> {
    // The table's copy first (`evt.policy.previewed`), this client's own draft second. Anyone who READ
    // the proposal can adopt it - the drafter is not the only seat allowed to act on a rule the whole
    // room can see - and the local map is only the drafter's head start on their own broadcast.
    const previewed = this.store
      .getSession(this.active ?? '')
      ?.policyPreviews.find((c) => c.id === id);
    const card = previewed ?? this.drafts.get(id);
    if (!card) {
      return this.print(t('policy.noDraft', { id }));
    }

    await this.sendGameAction({
      kind: 'proposeCompiledPolicy',
      id: card.id,
      name: card.name,
      prose: card.prose,
      effects: card.effects,
      downsideEffects: card.downsideEffects,
      processTaxRounds: card.processTaxRounds,
      seat: this.store.getSession(this.active ?? '')?.game?.you?.seat ?? '',
    });
  }

  async compileOkr(goal: string): Promise<void> {
    if (!this.active) return this.print(t('app.noActiveSession'));
    try {
      const s = await this.sessions.compileOkr(this.active, goal);
      if (!s.objective && s.keyResults.length === 0) {
        // The design's degrade: say so, and point at the command that was always there.
        this.print(t('okr.none'));
        return;
      }

      if (s.objective) {
        this.print(t('okr.objective', { objective: s.objective }));
      }
      for (const kr of s.keyResults) {
        this.print(t('okr.keyResult', { description: kr.description }));
        this.print(t('okr.adopt', { id: kr.id, expression: kr.expression }));
      }
      if (s.coaching) {
        this.print(t('okr.coaching', { coaching: s.coaching }));
      }
      if (s.rejected > 0) {
        // Shown rather than hidden: a compiler that quietly drops half its answer teaches nobody why.
        this.print(t('okr.rejected', { n: s.rejected }));
      }
    } catch (error) {
      const message = errMsg(error);
      this.print(t('okr.failed', { error: message }));
      const wait = this.retryHint(error);
      if (wait) this.print(wait);
      const action = refusalAction(message);
      if (action) {
        this.print(action);
      }
    }
  }

  async showRetro(): Promise<void> {
    if (!this.active)
      return this.print('no active session – type "list" then a number, or: create <name> game');
    try {
      // Fetch on demand and store it — the same slice the session-end `stream.retro` broadcast fills, so the
      // session view renders one retro regardless of how it arrived.
      this.store.applyRetro(this.active, await this.sessions.retro(this.active));

      // The private slice is fetched separately and is allowed not to exist: it is written after the
      // shared retro, and a 404 means "not yet" rather than "something went wrong".
      try {
        this.store.applyPersonalRetro(this.active, await this.sessions.personalRetro(this.active));
      } catch {
        // No slice for this player (they held no seat, or it is still being written).
      }
    } catch (error) {
      this.print(t('app.retroFailed', { error: errMsg(error) }));
    }
  }

  async sendGameAction(input: GameActionPayload): Promise<void> {
    if (this.isReadOnly()) return this.print('reconnecting… (read-only until the server confirms)');
    if (!this.active)
      return this.print('no active session – type "list" then a number, or: create <name> game');
    try {
      const ack = await this.ws.sendGameAction(this.active, input);
      if (ack.status === 'rejected') {
        this.gameRefused(t('app.gameRefused', { message: ack.error?.message ?? 'rejected' }));
      }
      // On accept the projected stream.state.delta re-renders the board (no echo needed).
    } catch (error) {
      this.gameRefused(t('app.gameActionFailed', { error: errMsg(error) }));
    }
  }

  /**
   * Prints a refused game action, and for the one refusal a player can actually act on, says what to do.
   *
   * "this session is not a game session" is accurate and useless on its own: the cause is a choice made
   * at create time, several minutes earlier, and the fix is not editing this session - a session without
   * a scenario can never get one. Naming the fix here is cheaper than the alternative, which is a player
   * typing /start repeatedly at a room that will never start.
   */
  private gameRefused(message: string): void {
    this.print(message);

    // The engine states the fact; the client names the move (standards/messages.md §4). A refusal with
    // no known move gets no invented one - "try again" for something that will refuse again is the
    // failure the standard calls worse than silence.
    const action = refusalAction(message);
    if (action) {
      this.print(action);
    }
  }

  async leaveSession(): Promise<void> {
    if (this.active) {
      try {
        await this.sessions.leave(this.active);
      } catch (error) {
        // Nothing for the player to do: the client leaves regardless, which is what the line has to say
        // so nobody sits waiting for a second attempt that is not coming.
        this.print(t('app.leaveFailed', { error: errMsg(error) }));
      }
    }
    this.active = null;
    this.ws.reconnect();
    this.goTo('login');
  }

  goTo(view: ViewName): void {
    this.shell.setView(view);
  }

  /** Navigate to the standalone diagnostics doctor (web-client.md §8). */
  openDiagnostics(): void {
    try {
      globalThis.location.hash = '#/diagnostics';
    } catch {
      this.print(t('app.diagUnavailable'));
    }
  }

  /** Apply a pending soft update: activate the waiting SW and reload (web-client.md §7). */
  applyUpdate(): void {
    this.print(t('app.reloading'));
    this.pwa.forceReload();
  }

  print(message: string): void {
    for (const line of message.split('\n')) this.logLines.push(line);
    if (this.logLines.length > LOG_LIMIT) {
      this.logLines = this.logLines.slice(this.logLines.length - LOG_LIMIT);
    }
    this.shell.render();
  }

  private async handleAuthError(error: ApiError): Promise<void> {
    this.print(t('app.authRefreshing', { code: error.code }));
    try {
      await this.tokens.refreshNow();
      this.ws.reconnect();
    } catch {
      this.tokens.clear();
      this.print(t('app.sessionExpired'));
      this.goTo('login');
    }
  }
}
